import React, { useEffect, useState } from "react";
import Select from "react-select";
import { academylist } from "../../../services/admin";
import {
  stucategorylist,
  stureligionlist,
  stucastelist,
  stusubcastelist,
  stumediumlist,
  stuclasslist,
  studivisionlist,
  studesilist,
  stureporttolist,
  stuappointlist,
  stushiftlist,
  stuquallist,
  studeptlist,
  stusubjectlist,
  stuacademicyearlist,
  stufeetypelist,
} from "../../../services/master";

// One report page for every master. Pick a master from the dropdown and its
// list is rendered in a timetable-styled table with column defs taken from the
// master's own screen. `needsForm` masters expect a FormData body (and accept
// an academy_id filter for super-admins); `transform` massages rows before they
// reach the table (e.g. feetype's coded values).
const MASTERS = [
  {
    key: "category",
    label: "Category",
    listFn: stucategorylist,
    columns: [{ headerName: "Category", field: "cat_name" }],
  },
  {
    key: "religion",
    label: "Religion",
    listFn: stureligionlist,
    columns: [{ headerName: "Religion", field: "rel_name" }],
  },
  {
    key: "caste",
    label: "Caste",
    listFn: stucastelist,
    columns: [{ headerName: "Caste", field: "caste_name" }],
  },
  {
    key: "subcaste",
    label: "Sub Caste",
    listFn: stusubcastelist,
    columns: [{ headerName: "Sub Caste", field: "subcaste_name" }],
  },
  {
    key: "medium",
    label: "Medium",
    listFn: stumediumlist,
    columns: [{ headerName: "Medium", field: "medium_name" }],
  },
  {
    key: "class",
    label: "Class",
    listFn: stuclasslist,
    columns: [{ headerName: "Class", field: "class_name" }],
  },
  {
    key: "division",
    label: "Section",
    listFn: studivisionlist,
    columns: [{ headerName: "Section", field: "name" }],
  },
  {
    key: "designation",
    label: "Designation",
    listFn: studesilist,
    columns: [{ headerName: "Designation", field: "desi_name" }],
  },
  {
    key: "reporting",
    label: "Reporting",
    listFn: stureporttolist,
    columns: [{ headerName: "Reporting", field: "report_name" }],
  },
  {
    key: "appointment",
    label: "Appointment Type",
    listFn: stuappointlist,
    columns: [{ headerName: "Appointment Type", field: "appoint_name" }],
  },
  {
    key: "shift",
    label: "Shift",
    listFn: stushiftlist,
    columns: [
      { headerName: "Shift", field: "shift_name" },
      { headerName: "In Time", field: "InTime" },
      { headerName: "Out Time", field: "OutTime" },
    ],
  },
  {
    key: "qualification",
    label: "Qualification",
    listFn: stuquallist,
    columns: [{ headerName: "Qualification", field: "qual_name" }],
  },
  {
    key: "department",
    label: "Department",
    listFn: studeptlist,
    columns: [{ headerName: "Department", field: "dept_name" }],
  },
  {
    key: "subject",
    label: "Subject",
    listFn: stusubjectlist,
    needsForm: true,
    columns: [
      { headerName: "Subject", field: "subject_name" },
      { headerName: "Academy", field: "academy_name" },
    ],
  },
  {
    key: "academicyear",
    label: "Academic Year",
    listFn: stuacademicyearlist,
    needsForm: true,
    columns: [
      { headerName: "Academic Year", field: "aca_year" },
      { headerName: "Academy", field: "academy_name" },
    ],
  },
  {
    key: "feetype",
    label: "Fee Type",
    listFn: stufeetypelist,
    needsForm: true,
    transform: (rows) =>
      rows.map((item) => ({
        ...item,
        feestype:
          item.feestype == 1
            ? "Common"
            : item.feestype == 2
            ? "Hostel"
            : item.feestype == 3
            ? "Bus"
            : "",
      })),
    columns: [
      { headerName: "Fee Type", field: "fee_type" },
      { headerName: "Type", field: "feestype" },
      { headerName: "Route", field: "vehroute_name" },
      { headerName: "Academy", field: "academy_name" },
    ],
  },
];

const MasterReport = () => {
  const [masterKey, setMasterKey] = useState(MASTERS[0].key);
  const [rowData, setRowData] = useState([]);
  const [aclist, setACList] = useState([]);
  const [acaid, setAcaID] = useState("");

  const selected = MASTERS.find((m) => m.key === masterKey) || MASTERS[0];

  useEffect(() => {
    academylist().then((res) => {
      if (res.status === true && res.data.details.length > 0) {
        const arr = res.data.details.map((item) => ({
          value: item.id,
          label: item.name + " - " + item.code,
        }));
        setACList(arr);

        // Default to the academy stored at login (matches other report screens).
        let defaultAca = "";
        if (localStorage.getItem("AAcademy") != null) {
          defaultAca =
            arr.find(
              (a) => a.value == localStorage.getItem("AAcademy")
            ) || "";
          if (defaultAca) setAcaID(defaultAca);
        }
        loadMaster(masterKey, defaultAca);
      } else {
        loadMaster(masterKey, "");
      }
    });
  }, []);

  function loadMaster(key, academy) {
    const master = MASTERS.find((m) => m.key === key);
    if (!master) return;
    setRowData([]);

    let arg;
    if (master.needsForm) {
      arg = new FormData();
      if (localStorage.getItem("Role") === "1" && academy && academy.value) {
        arg.append("academy_id", academy.value);
      }
    }

    master.listFn(arg).then((res) => {
      if (res && res.status === true) {
        const data = res.data || [];
        setRowData(master.transform ? master.transform(data) : data);
      }
    });
  }

  function handleAcademy(e) {
    setAcaID(e || "");
    loadMaster(masterKey, e || "");
  }

  const exportToCSV = () => {
    const header = selected.columns.map((c) => c.headerName);
    const lines = rowData.map((row) =>
      selected.columns
        .map((c) => {
          const val = row[c.field] == null ? "" : String(row[c.field]);
          return `"${val.replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${selected.label} Report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="main-content-title tx-24 mg-b-5">Master Report</h2>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a>Reports</a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Master Report
          </li>
        </ol>
      </div>
      <div className="content-body">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h2 className="main-content-title tx-24 mg-b-5">Master Report</h2>
            </div>
            <div className="card-body">
              <div className="row">
                {localStorage.getItem("Role") === "1" && (
                  <div className="col-xl-4 col-sm-6">
                    <label className="form-label">Select Academy</label>
                    <Select
                      className="default-select"
                      placeholder="Select Academy"
                      value={acaid}
                      options={aclist}
                      onChange={handleAcademy}
                      isSearchable={true}
                      isClearable
                    />
                  </div>
                )}

                <div className="col-xl-4 col-sm-6">
                  <label className="form-label">Master</label>
                  <select
                    className="default-select form-control wide"
                    value={masterKey}
                    onChange={(e) => {
                      setMasterKey(e.target.value);
                      loadMaster(e.target.value, acaid);
                    }}
                  >
                    {MASTERS.map((m) => (
                      <option value={m.key} key={m.key}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-xl-4 col-sm-6">
                  <div className="res-right mt-5">
                    <button
                      className="btn btn-success me-2"
                      onClick={exportToCSV}
                    >
                      Excel Download
                    </button>
                  </div>
                </div>
              </div>

              <div className="timetable">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      {selected.columns.map((col) => (
                        <th key={col.field}>{col.headerName}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rowData.length === 0 ? (
                      <tr>
                        <td colSpan={selected.columns.length + 1}>
                          No records found
                        </td>
                      </tr>
                    ) : (
                      rowData.map((row, index) => (
                        <tr
                          key={row.id || index}
                          className={index % 2 === 0 ? "even" : "odd"}
                        >
                          <td>{index + 1}</td>
                          {selected.columns.map((col) => (
                            <td key={col.field}>{row[col.field]}</td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterReport;
